import React, { useState, useEffect } from "react";
import { baseurl } from "../../baseurl";
import axios from "axios";
import { FiPhone, FiMail, FiMapPin, FiHash, FiCalendar, FiFileText, FiTag } from "react-icons/fi";

/* ─────────────────────────────────────────────
   INLINE CSS — "Mosaic" design
   Diagonal split header + zebra table + accent badges
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

.it5__wrap * { box-sizing: border-box; margin: 0; padding: 0; }

.it5__wrap {
  font-family: 'DM Sans', sans-serif;
  background: #dde8e8;
  min-height: 100vh;
 
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

/* ── A4 paper ── */
.it5__paper {
  width: 210mm;
  min-height: 297mm;
  background: #ffffff;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(10,85,86,0.18), 0 2px 8px rgba(10,85,86,0.10);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

/* ═══════════════════════════════
   DIAGONAL HEADER
═══════════════════════════════ */
.it5__header {
  position: relative;
  background: #ffffff;
  padding: 0;
  overflow: hidden;
  min-height: 180px;
}

/* Left teal panel */
.it5__hdr-left {
  position: absolute;
  top: 0; left: 0; bottom: 0;
  width: 58%;
  background: linear-gradient(135deg, #0a3d3e 0%, #0a5556 70%, #0d7070 100%);
  clip-path: polygon(0 0, 100% 0, 82% 100%, 0 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 32px 80px 32px 40px;
  z-index: 1;
}

/* Right side (invoice meta) */
.it5__hdr-right {
  position: absolute;
  top: 0; right: 0; bottom: 0;
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  padding: 32px 40px;
  z-index: 0;
}

/* decorative circles top-right */
.it5__hdr-circle1 {
  position: absolute; top: -40px; right: -30px;
  width: 130px; height: 130px; border-radius: 50%;
  background: rgba(10,85,86,0.06);
  z-index: 0;
}
.it5__hdr-circle2 {
  position: absolute; top: 20px; right: 60px;
  width: 70px; height: 70px; border-radius: 50%;
  background: rgba(255,209,102,0.10);
  z-index: 0;
}

/* brand inside left panel */
.it5__brand-init {
  width: 52px; height: 52px; border-radius: 14px; flex-shrink: 0;
  background: rgba(255,255,255,0.14); border: 1.5px solid rgba(255,255,255,0.22);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Space Grotesk', sans-serif; font-size: 22px; font-weight: 800;
  color: #FFD166; margin-bottom: 12px;
}
.it5__company-name {
  font-family: 'Space Grotesk', sans-serif; font-size: 20px; font-weight: 800;
  color: #ffffff; letter-spacing: -0.02em; margin-bottom: 8px; line-height: 1.15;
}
.it5__seller-row {
  display: flex; align-items: center; gap: 6px;
  font-size: 11.5px; color: rgba(255,255,255,0.60); margin-bottom: 4px;
}
.it5__seller-row svg { color: #FFD166; flex-shrink: 0; }
.it5__gstin-chip {
  display: inline-flex; align-items: center;
  padding: 3px 10px; border-radius: 6px; margin-top: 4px;
  background: rgba(255,209,102,0.15); border: 1px solid rgba(255,209,102,0.30);
  font-family: 'Space Grotesk', sans-serif; font-size: 10px; font-weight: 700;
  color: #FFD166; letter-spacing: 0.04em;
}

/* invoice word + meta (right) */
.it5__inv-word {
  font-family: 'Space Grotesk', sans-serif; font-size: 40px; font-weight: 800;
  color: #0a5556; letter-spacing: 0.06em; line-height: 1; margin-bottom: 14px;
  position: relative; z-index: 1;
}
.it5__inv-word::after {
  content: '';
  display: block; width: 40px; height: 3px;
  background: linear-gradient(90deg, #0a5556, #FFD166);
  margin-top: 8px; margin-left: auto; border-radius: 2px;
}
.it5__meta-block { display: flex; flex-direction: column; gap: 7px; position: relative; z-index: 1; }
.it5__meta-row { display: flex; align-items: center; justify-content: flex-end; gap: 8px; }
.it5__meta-lbl {
  font-size: 10px; font-family: 'Space Grotesk', sans-serif; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase; color: #9ababa; text-align: right;
}
.it5__meta-val {
  font-family: 'Space Grotesk', sans-serif; font-size: 13px; font-weight: 700;
  color: #0f2a2a; min-width: 110px; text-align: right;
}

/* status */
.it5__status {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 12px; border-radius: 50px;
  font-size: 11px; font-family: 'Space Grotesk', sans-serif; font-weight: 700;
}
.it5__status--paid    { background: #d1fae5; color: #065f46; border: 1px solid #6ee7b7; }
.it5__status--unpaid  { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
.it5__status--partial { background: #fef3c7; color: #92400e; border: 1px solid #fcd34d; }
.it5__status--pending { background: #f0f9ff; color: #075985; border: 1px solid #7dd3fc; }
.it5__status-dot { width: 6px; height: 6px; border-radius: 50%; }

/* ── ACCENT STRIP below header ── */
.it5__accent-strip {
  height: 4px;
  background: linear-gradient(90deg, #FFD166 0%, #14b8a6 40%, #0a5556 100%);
}

/* ═══════════════════════════════
   INFO BAND (billing + details)
═══════════════════════════════ */
.it5__info-band {
  display: grid; grid-template-columns: 1fr 1fr;
  background: #f7fbfb;
  border-bottom: 1px solid rgba(10,85,86,0.08);
}
.it5__info-col {
  padding: 22px 36px;
}
.it5__info-col + .it5__info-col {
  border-left: 1px solid rgba(10,85,86,0.08);
}
.it5__info-lbl {
  font-family: 'Space Grotesk', sans-serif; font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: #fff; background: #0a5556;
  display: inline-block; padding: 3px 9px; border-radius: 4px; margin-bottom: 12px;
}
.it5__client-name {
  font-family: 'Space Grotesk', sans-serif; font-size: 16px; font-weight: 800;
  color: #0f2a2a; margin-bottom: 8px;
}
.it5__client-row {
  display: flex; align-items: flex-start; gap: 6px;
  font-size: 12px; color: #6a8c8c; margin-bottom: 5px; line-height: 1.4;
}
.it5__client-row svg { color: #14b8a6; flex-shrink: 0; margin-top: 1px; }
.it5__gstin-tag {
  display: inline-block; padding: 2px 8px; border-radius: 5px;
  font-size: 10.5px; font-weight: 700;
  background: #eef6f6; color: #0a5556; border: 1px solid rgba(10,85,86,0.16); margin-top: 4px;
}

/* detail list */
.it5__det-item {
  display: flex; align-items: center; gap: 10px; padding: 8px 0;
  border-bottom: 1px solid rgba(10,85,86,0.06);
}
.it5__det-item:last-child { border-bottom: none; }
.it5__det-icon {
  width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0;
  background: #eef6f6; border: 1px solid rgba(10,85,86,0.10);
  display: flex; align-items: center; justify-content: center; color: #0a5556;
}
.it5__det-lbl { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: #9ababa; margin-bottom: 1px; }
.it5__det-val { font-family: 'Space Grotesk', sans-serif; font-size: 13px; font-weight: 700; color: #0f2a2a; }

/* ═══════════════════════════════
   ITEMS TABLE
═══════════════════════════════ */
.it5__table-wrap { padding: 26px 36px; flex: 1; }
.it5__table-hd {
  font-size: 9.5px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
  color: #9ababa; margin-bottom: 12px;
}
.it5__table { width: 100%; border-collapse: collapse; border-radius: 12px; overflow: hidden; border: 1px solid rgba(10,85,86,0.08); }

.it5__table thead tr {
  background: #0a5556;
}
.it5__table thead th {
  font-family: 'Space Grotesk', sans-serif; font-size: 10px; font-weight: 700;
  letter-spacing: 0.09em; text-transform: uppercase; color: rgba(255,255,255,0.80);
  padding: 12px 14px; text-align: left; white-space: nowrap;
}
.it5__table thead th:first-child { padding-left: 18px; }
.it5__table thead th.th-r { text-align: right; padding-right: 18px; }
.it5__table thead th.th-c { text-align: center; }

/* zebra rows */
.it5__table tbody tr:nth-child(even) { background: #f7fbfb; }
.it5__table tbody tr:nth-child(odd)  { background: #ffffff; }
.it5__table tbody tr { border-bottom: 1px solid rgba(10,85,86,0.04); transition: background 0.12s; }
.it5__table tbody tr:last-child { border-bottom: none; }
.it5__table tbody tr:hover { background: #eef7f7 !important; }

.it5__table tbody td { padding: 13px 14px; font-size: 13px; color: #0f2a2a; vertical-align: middle; }
.it5__table tbody td:first-child { padding-left: 18px; }
.it5__table tbody td.td-c { text-align: center; font-family: 'Space Grotesk', sans-serif; font-weight: 700; }
.it5__table tbody td.td-r { text-align: right; padding-right: 18px; color: #6a8c8c; font-weight: 600; }
.it5__table tbody td.td-total { text-align: right; padding-right: 18px; }

.it5__item-name { font-family: 'Space Grotesk', sans-serif; font-size: 13px; font-weight: 700; color: #0f2a2a; margin-bottom: 3px; }
.it5__badges { display: flex; gap: 5px; flex-wrap: wrap; }
.it5__badge-hsn {
  padding: 2px 8px; border-radius: 50px; font-size: 10px; font-weight: 700;
  background: rgba(10,85,86,0.07); color: #0a5556; border: 1px solid rgba(10,85,86,0.14);
}
.it5__badge-tax {
  padding: 2px 8px; border-radius: 50px; font-size: 10px; font-weight: 700;
  background: rgba(255,209,102,0.15); color: #8a6500; border: 1px solid rgba(255,209,102,0.35);
}
.it5__total-val {
  font-family: 'Space Grotesk', sans-serif; font-size: 14px; font-weight: 800; color: #0a5556;
}

/* ═══════════════════════════════
   FOOTER
═══════════════════════════════ */
.it5__footer {
  display: flex; align-items: stretch; gap: 0;
  border-top: 1px solid rgba(10,85,86,0.09);
  margin-top: auto;
}
.it5__notes {
  flex: 1; padding: 20px 36px;
  border-right: 1px solid rgba(10,85,86,0.09);
}
.it5__notes-lbl {
  font-size: 9.5px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
  color: #9ababa; margin-bottom: 8px;
}
.it5__notes-txt { font-size: 12.5px; color: #6a8c8c; line-height: 1.6; }

/* summary panel */
.it5__summary-panel { width: 240px; flex-shrink: 0; display: flex; flex-direction: column; }
.it5__sum-rows { flex: 1; }
.it5__sum-row {
  display: flex; justify-content: space-between;
  padding: 10px 22px;
  border-bottom: 1px solid rgba(10,85,86,0.06);
  font-size: 12.5px;
}
.it5__sum-lbl { color: #6a8c8c; font-weight: 500; }
.it5__sum-val { font-family: 'Space Grotesk', sans-serif; font-weight: 700; color: #0f2a2a; }

/* grand total */
.it5__grand {
  padding: 0;
}
.it5__grand-inner {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 18px 22px;
  background: linear-gradient(135deg, #0a3d3e, #0a5556);
  text-align: center;
}
.it5__grand-lbl {
  font-family: 'Space Grotesk', sans-serif; font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase; color: rgba(255,255,255,0.50); margin-bottom: 4px;
}
.it5__grand-val {
  font-family: 'Space Grotesk', sans-serif; font-size: 22px; font-weight: 800;
  color: #FFD166; letter-spacing: -0.01em;
}

/* ── BOTTOM STRIP ── */
.it5__bottom {
  height: 4px;
  background: linear-gradient(90deg, #0a3d3e 0%, #0a5556 30%, #14b8a6 65%, #FFD166 100%);
}

/* print */
@page { size: A4 portrait; margin: 0; }
@media print {
  .it5__wrap { background: none !important; padding: 0 !important; display: block !important; }
  .it5__paper { width: 210mm !important; min-height: 297mm !important; box-shadow: none !important; margin: 0 !important; }
}
@media (max-width: 640px) {
  .it5__hdr-left { width: 100%; clip-path: none; padding: 24px 20px; position: relative; }
  .it5__hdr-right { position: relative; width: 100%; padding: 20px; align-items: flex-start; }
  .it5__header { min-height: auto; }
  .it5__info-band { grid-template-columns: 1fr; }
  .it5__footer { flex-direction: column; }
  .it5__summary-panel { width: 100%; }
}
`;

/* ─── helpers ─── */
const num   = (v) => { const n = Number(v); return isNaN(n) ? 0 : n; };
const fmtIN = (v) => num(v).toLocaleString("en-IN", { minimumFractionDigits: 2 });
const init  = (name = "") => name.trim()[0]?.toUpperCase() || "?";

const getStatusInfo = (status = "") => {
  const s = status.toLowerCase();
  if (s === "paid")    return { cls: "it5__status--paid",    dot: "#10b981" };
  if (s === "unpaid")  return { cls: "it5__status--unpaid",  dot: "#ef4444" };
  if (s === "partial") return { cls: "it5__status--partial", dot: "#f59e0b" };
  return                      { cls: "it5__status--pending", dot: "#38bdf8" };
};

/* ═══════════════════════════════════════════════════
   COMPONENT — EXACT SAME LOGIC AS InvoiceThree
═══════════════════════════════════════════════════ */
const InvoiceFive = ({ invoiceData }) => {
  const [sellerInfo, setSellerInfo] = useState(null);
  const token = localStorage.getItem("auth_token");

  console.log("invoiceData", invoiceData);

  /* ── FETCH BUSINESS INFO ── */
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await axios.get(`${baseurl}getBusinessInfo`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("response", response);
        if (response.data.success) {
          setSellerInfo(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching business info for seller:", error);
      }
    };
    fetchBusinessInfo();
  }, [token]);

  /* ── DEFAULT DATA ── */
  const defaultData = {
    invoice_number: "INV-000",
    invoice_date: new Date().toISOString().split("T")[0],
    due_date: "N/A",
    payment_status: "Pending",
    notes: "Thank you for your business.",
    seller: { name: "N/A", gstin: "N/A", phone: "N/A", state: "N/A", email: "", zipcode: "" },
    customer: { name: "Client Name", email: "client@example.com", gstin: "N/A", phone: "N/A", state: "N/A", address: "Client Address Here" },
    items: [],
    gst_breakdown: { CGST: 0, SGST: 0 },
    subtotal: 0,
    taxable_amount: 0,
    grand_total: 0,
  };

  /* ── FINAL RESOLVED DATA ── */
  const resolvedSeller =
    invoiceData?.invoice_data?.seller ||
    (sellerInfo ? {
      name:    sellerInfo.business_name,
      gstin:   sellerInfo.gst || "N/A",
      phone:   sellerInfo.whatsapp_number,
      state:   sellerInfo.state,
      city:    sellerInfo.city,
      zipcode: sellerInfo.zipcode,
      email:   sellerInfo.contact_email || sellerInfo.email,
    } : defaultData.seller);

  const resolved = invoiceData?.invoice_data
    ? {
        invoice_number:  invoiceData.invoice_number || defaultData.invoice_number,
        invoice_date:    invoiceData.invoice_data.invoiceDate || defaultData.invoice_date,
        due_date:        invoiceData.invoice_data.dueDate,
        payment_status:  invoiceData.invoice_data.paymentStatus,
        seller:          resolvedSeller,
        customer:        invoiceData.invoice_data.customer || defaultData.customer,
        items:           invoiceData.invoice_data.items || [],
        gst_breakdown:   invoiceData.invoice_data.gstBreakdown || { CGST: 0, SGST: 0 },
        subtotal:        num(invoiceData.invoice_data.itemSubtotal),
        taxable_amount:  num(invoiceData.invoice_data.taxableAmount),
        grand_total:     num(invoiceData.grand_total ?? invoiceData.invoice_data.grandTotal),
        notes:           invoiceData.invoice_data.notes || defaultData.notes,
      }
    : { ...defaultData, seller: resolvedSeller };

  const {
    invoice_number, invoice_date, payment_status,
    seller, customer, items, gst_breakdown,
    subtotal, grand_total, notes,
  } = resolved;

  const si = getStatusInfo(payment_status);

  return (
    <>
      <style>{CSS}</style>
      <div className="it5__wrap">
        <div className="it5__paper">

          {/* ── DIAGONAL HEADER ── */}
          <header className="it5__header">
            {/* decorative circles */}
            <div className="it5__hdr-circle1" />
            <div className="it5__hdr-circle2" />

            {/* Left teal panel */}
            <div className="it5__hdr-left">
              <div className="it5__brand-init">{init(seller.name)}</div>
              <div className="it5__company-name">{seller.name}</div>
              <div>
                {(seller.city || seller.state) && (
                  <div className="it5__seller-row"><FiMapPin size={11} />{seller.city && `${seller.city}, `}{seller.state} {seller.zipcode}</div>
                )}
                {seller.phone && <div className="it5__seller-row"><FiPhone size={11} />{seller.phone}</div>}
                {seller.email && <div className="it5__seller-row"><FiMail  size={11} />{seller.email}</div>}
                {seller.gstin !== "N/A" && <div className="it5__gstin-chip">GSTIN: {seller.gstin}</div>}
              </div>
            </div>

            {/* Right meta */}
            <div className="it5__hdr-right">
              <div className="it5__inv-word">INVOICE</div>
              <div className="it5__meta-block">
                <div className="it5__meta-row">
                  <span className="it5__meta-lbl">No.</span>
                  <span className="it5__meta-val">{invoice_number}</span>
                </div>
                <div className="it5__meta-row">
                  <span className="it5__meta-lbl">Date</span>
                  <span className="it5__meta-val">{invoice_date}</span>
                </div>
                <div className="it5__meta-row">
                  <span className="it5__meta-lbl">Due</span>
                  <span className="it5__meta-val">{resolved.due_date || "N/A"}</span>
                </div>
                <div className="it5__meta-row">
                  <span className="it5__meta-lbl">Status</span>
                  <span className="it5__meta-val">
                    <span className={`it5__status ${si.cls}`}>
                      <span className="it5__status-dot" style={{ background: si.dot }} />
                      {payment_status}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </header>

          {/* accent strip */}
          <div className="it5__accent-strip" />

          {/* ── INFO BAND ── */}
          <div className="it5__info-band">
            <div className="it5__info-col">
              <div className="it5__info-lbl">Bill To</div>
              <div className="it5__client-name">{customer.name}</div>
              {customer.address && <div className="it5__client-row"><FiMapPin size={11} />{customer.address}</div>}
              {customer.phone   && <div className="it5__client-row"><FiPhone  size={11} />{customer.phone}</div>}
              {customer.email   && <div className="it5__client-row"><FiMail   size={11} />{customer.email}</div>}
              {customer.gstin !== "N/A" && customer.gstin && (
                <span className="it5__gstin-tag">GSTIN: {customer.gstin}</span>
              )}
            </div>
            <div className="it5__info-col">
              <div className="it5__info-lbl">Invoice Info</div>
              <div className="it5__det-item">
                <div className="it5__det-icon"><FiHash size={13} /></div>
                <div>
                  <div className="it5__det-lbl">Invoice Number</div>
                  <div className="it5__det-val">{invoice_number}</div>
                </div>
              </div>
              <div className="it5__det-item">
                <div className="it5__det-icon"><FiCalendar size={13} /></div>
                <div>
                  <div className="it5__det-lbl">Issue Date</div>
                  <div className="it5__det-val">{invoice_date}</div>
                </div>
              </div>
              <div className="it5__det-item">
                <div className="it5__det-icon"><FiCalendar size={13} /></div>
                <div>
                  <div className="it5__det-lbl">Due Date</div>
                  <div className="it5__det-val">{resolved.due_date || "N/A"}</div>
                </div>
              </div>
            </div>
          </div>

          {/* ── ITEMS TABLE ── */}
          <div className="it5__table-wrap">
            <div className="it5__table-hd">Line Items</div>
            <table className="it5__table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="th-c">Qty</th>
                  <th className="th-r">Rate</th>
                  <th className="th-r">Total (incl. GST)</th>
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? items.map((item, i) => {
                  const lineTotal = num(item.qty) * num(item.rate) * (1 + num(item.tax) / 100);
                  return (
                    <tr key={i}>
                      <td>
                        <div className="it5__item-name">{item.description}</div>
                        <div className="it5__badges">
                          <span className="it5__badge-hsn">HSN {item.hsn || "—"}</span>
                          <span className="it5__badge-tax">{item.tax}% GST</span>
                        </div>
                      </td>
                      <td className="td-c">{item.qty}</td>
                      <td className="td-r">₹{fmtIN(item.rate)}</td>
                      <td className="td-total">
                        <span className="it5__total-val">₹{fmtIN(lineTotal)}</span>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan="4" style={{ textAlign: "center", padding: "32px", color: "#9ababa", fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700 }}>No items found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── FOOTER ── */}
          <div className="it5__footer">
            <div className="it5__notes">
              <div className="it5__notes-lbl">Notes & Remarks</div>
              <div className="it5__notes-txt">{notes || "Thank you for your business."}</div>
            </div>
            <div className="it5__summary-panel">
              <div className="it5__sum-rows">
                <div className="it5__sum-row">
                  <span className="it5__sum-lbl">Subtotal</span>
                  <span className="it5__sum-val">₹{fmtIN(subtotal)}</span>
                </div>
                <div className="it5__sum-row">
                  <span className="it5__sum-lbl">CGST</span>
                  <span className="it5__sum-val">₹{fmtIN(gst_breakdown.CGST)}</span>
                </div>
                <div className="it5__sum-row">
                  <span className="it5__sum-lbl">SGST</span>
                  <span className="it5__sum-val">₹{fmtIN(gst_breakdown.SGST)}</span>
                </div>
              </div>
              <div className="it5__grand">
                <div className="it5__grand-inner">
                  <div className="it5__grand-lbl">Amount Due</div>
                  <div className="it5__grand-val">₹{fmtIN(grand_total)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* ── BOTTOM STRIP ── */}
          <div className="it5__bottom" />

        </div>
      </div>
    </>
  );
};

export default InvoiceFive;
